import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import LeadsAndTrialsSection from '@/components/admin/LeadsAndTrialsSection';
import UsersManagementSection from '@/components/admin/UsersManagementSection';
import CheckoutAnalyticsSection from '@/components/admin/CheckoutAnalyticsSection';
import DataQualityAuditSection from '@/components/admin/DataQualityAuditSection';
import {
  Bloque,
  BloqueVendiendo,
  BloqueUso,
  BloqueTareas,
  AutorizarEnLote,
  fmtMoney,
  fmtFecha,
} from '@/components/admin/PanelNegocio';
import { RefreshCw, LogOut, ShieldAlert, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_SECRET_KEY = 'evalpro_admin_secret';
const PERIODO_KEY = 'evalpro_admin_periodo';

const PERIODOS = [
  ['7d', '7 días'],
  ['30d', '30 días'],
  ['90d', '90 días'],
  ['mes', 'Este mes'],
  ['mes_anterior', 'Mes pasado'],
];

function leerPeriodo() {
  try {
    const p = localStorage.getItem(PERIODO_KEY);
    return PERIODOS.some(([k]) => k === p) ? p : '30d';
  } catch {
    return '30d';
  }
}

function StatusBadge({ status }) {
  const map = {
    approved: { cls: 'bg-[#5B9B2F]/15 text-[#5B9B2F] border-[#5B9B2F]/30', label: 'Aprobado' },
    pending: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', label: 'Pendiente' },
    in_process: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', label: 'En proceso' },
    rejected: { cls: 'bg-red-500/15 text-red-400 border-red-500/30', label: 'Rechazado' },
    cancelled: { cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30', label: 'Cancelado' },
    refunded: { cls: 'bg-purple-500/15 text-purple-400 border-purple-500/30', label: 'Reembolsado' },
  };
  const s = map[status] || { cls: 'bg-white/5 text-slate-400 border-white/10', label: status || '—' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${s.cls}`}>
      {s.label}
    </span>
  );
}

/**
 * En qué entorno está este panel y si Mercado Pago cobra de verdad: con dos
 * entornos abiertos en pestañas es fácil confundirse.
 */
function EstadoEntorno({ entorno }) {
  const esPrueba = entorno.nombre === 'prueba';
  const mp = {
    real: ['cobra de verdad', 'text-[#7BC24A]'],
    prueba: ['en modo prueba', 'text-amber-300'],
    sin_configurar: ['sin configurar', 'text-red-300'],
  }[entorno.mercado_pago] || [entorno.mercado_pago, 'text-slate-300'];
  return (
    <p className="mt-1 text-[11px] flex flex-wrap gap-x-3 gap-y-0.5" data-testid="estado-entorno">
      <span className={`font-bold ${esPrueba ? 'text-amber-300' : 'text-[#7BC24A]'}`}>
        {esPrueba ? 'PRUEBA' : 'PRODUCCIÓN'}
      </span>
      <span className="text-slate-400">
        Mercado Pago: <span className={mp[1]}>{mp[0]}</span>
      </span>
      <span className="text-slate-400">
        Mails:{' '}
        <span className={entorno.mails === 'configurados' ? 'text-[#7BC24A]' : 'text-red-300'}>
          {entorno.mails === 'configurados' ? 'configurados' : 'sin configurar'}
        </span>
      </span>
      {entorno.admin_por_mail !== undefined && (
        <span className="text-slate-400">
          Panel:{' '}
          <span className={entorno.admin_por_mail ? 'text-[#7BC24A]' : 'text-amber-300'}>
            {entorno.admin_por_mail ? 'solo tus mails' : 'solo con la clave (falta ADMIN_EMAILS)'}
          </span>
        </span>
      )}
    </p>
  );
}

/** Últimos 50 pagos tal como llegaron de Mercado Pago. Sirve cuando algo se rompe. */
function UltimosPagos({ pedir }) {
  const [pagos, setPagos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    pedir('/api/admin/dashboard/stats')
      .then((d) => { if (!cancelado) setPagos(d.last_payments || []); })
      .catch((e) => { if (!cancelado) setError(e.message); });
    return () => { cancelado = true; };
  }, [pedir]);

  return (
    <section className="mb-8">
      <h2 className="text-xs uppercase tracking-wider text-white font-semibold mb-3">Últimos pagos</h2>
      {error && <p className="text-red-300 text-sm">{error}</p>}
      {!pagos && !error && <p className="text-slate-500 text-sm">Cargando…</p>}
      {pagos && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="payments-table">
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Pago</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Monto</th>
                  <th className="px-4 py-3 text-center">CAPI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {pagos.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Todavía no hay pagos.</td></tr>
                )}
                {pagos.map((p) => (
                  <tr key={p.payment_id} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtFecha(p.date_approved || p.created_at, true)}</td>
                    <td className="px-4 py-3 text-slate-200 truncate max-w-[200px]">{p.payer_email || '—'}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{p.payment_id}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-right text-white font-semibold">{fmtMoney(p.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      {p.capi_sent ? <span className="text-[#5B9B2F] text-xs">✓</span> : <span className="text-slate-600 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [adminSecret, setAdminSecret] = useState(() => {
    try { return localStorage.getItem(ADMIN_SECRET_KEY) || ''; } catch { return ''; }
  });
  const [secretInput, setSecretInput] = useState('');
  const [periodo, setPeriodo] = useState(leerPeriodo);
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Lo técnico se monta solo al abrirlo: no pide datos si nadie lo mira.
  const [tecnicoAbierto, setTecnicoAbierto] = useState(false);

  const olvidarSecret = useCallback(() => {
    try { localStorage.removeItem(ADMIN_SECRET_KEY); } catch { /* noop */ }
    setAdminSecret('');
  }, []);

  // Todas las llamadas del panel: token + admin secret, y el mismo manejo de
  // sesión vencida (frecuente después de un redeploy que rota JWT_SECRET_KEY).
  const pedir = useCallback(async (path, opciones = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opciones,
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Admin-Secret': adminSecret,
        ...(opciones.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    if (res.status === 401) {
      toast.error('Tu sesión expiró. Ingresá de nuevo.');
      try { await logout(); } catch (_) { /* noop */ }
      navigate('/login?next=/admin/dashboard');
      throw new Error('Sesión vencida');
    }
    if (res.status === 403) {
      olvidarSecret();
      // Con ADMIN_EMAILS cargada, también responde 403 a una cuenta que no es de admin.
      throw new Error('Clave incorrecta, o entraste con una cuenta que no es de admin.');
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
    return data;
  }, [token, adminSecret, logout, navigate, olvidarSecret]);

  const cargar = useCallback(async () => {
    if (!adminSecret || !token) return;
    setLoading(true);
    setError(null);
    try {
      setPanel(await pedir(`/api/admin/panel?periodo=${periodo}`));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminSecret, token, periodo, pedir]);

  useEffect(() => { cargar(); }, [cargar]);

  const elegirPeriodo = (p) => {
    setPeriodo(p);
    try { localStorage.setItem(PERIODO_KEY, p); } catch { /* noop */ }
  };

  const guardarGasto = async (mes, monto) => {
    await pedir('/api/admin/panel/gasto-anuncios', { method: 'PUT', body: JSON.stringify({ mes, monto }) });
    await cargar();
  };

  const marcarCapacitacion = async (emails, estado) => {
    await pedir('/api/admin/panel/capacitacion', { method: 'POST', body: JSON.stringify({ emails, estado }) });
    await cargar();
  };

  const autorizar = async (texto) => {
    const r = await pedir('/api/admin/authorize-emails', { method: 'POST', body: JSON.stringify({ texto }) });
    cargar();
    return r;
  };

  const saveSecret = (e) => {
    e.preventDefault();
    if (!secretInput.trim()) return;
    try { localStorage.setItem(ADMIN_SECRET_KEY, secretInput.trim()); } catch { /* noop */ }
    setAdminSecret(secretInput.trim());
    toast.success('Admin secret guardado');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md text-center">
          <ShieldAlert className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Necesitás estar logueado</h1>
          <Button onClick={() => navigate('/login')} className="mt-4 bg-[#5B9B2F] hover:bg-[#4a8126]">
            Ir al login
          </Button>
        </div>
      </div>
    );
  }

  if (!adminSecret) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-4">
        <form onSubmit={saveSecret} className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md w-full">
          <ShieldAlert className="h-10 w-10 text-[#5B9B2F] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2 text-center">Acceso de Admin</h1>
          <p className="text-slate-400 text-sm text-center mb-6">Ingresá el admin secret para ver el panel.</p>
          <Input
            type="password"
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            placeholder="Admin secret"
            className="bg-white/5 border-white/10 text-white"
            data-testid="admin-secret-input"
            autoFocus
          />
          <Button
            type="submit"
            className="w-full mt-4 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-semibold"
            data-testid="admin-secret-submit"
          >
            Entrar
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white">
      <header className="border-b border-white/5 px-4 py-4 sticky top-0 z-10 bg-[#0D1B2A]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight" data-testid="admin-title">EVALPRO · Admin</h1>
            <p className="text-slate-500 text-xs">{user.email}</p>
            {panel?.entorno && <EstadoEntorno entorno={panel.entorno} />}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Un solo período para todo el panel */}
            <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="selector-periodo">
              {PERIODOS.map(([clave, nombre]) => (
                <button
                  key={clave}
                  type="button"
                  onClick={() => elegirPeriodo(clave)}
                  className={`px-2.5 py-1.5 text-xs font-medium transition ${
                    periodo === clave ? 'bg-[#5B9B2F] text-white' : 'text-slate-300 hover:bg-white/5'
                  }`}
                  data-testid={`periodo-${clave}`}
                >
                  {nombre}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={cargar}
              disabled={loading}
              data-testid="refresh-btn"
              className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate('/'); }}
              className="text-slate-400 hover:text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-300 text-sm">{error}</div>
        )}

        {!panel ? (
          <div className="text-center py-20 text-slate-400" data-testid="loading">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3" />
            Cargando el panel…
          </div>
        ) : (
          <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <p className="text-xs text-slate-500 mb-6">
              {panel.periodo_nombre}: del {fmtFecha(panel.desde)} al {fmtFecha(panel.hasta)}.
              Las comparaciones son contra el período anterior de la misma duración.
            </p>

            <BloqueVendiendo datos={panel.vendiendo} onGuardarGasto={guardarGasto} />
            <BloqueUso datos={panel.uso} />
            <BloqueTareas datos={panel.tareas} onMarcarCapacitacion={marcarCapacitacion} />

            <Bloque numero="4" pregunta="Herramientas" testid="bloque-herramientas">
              <div className="mb-6">
                <AutorizarEnLote onAutorizar={autorizar} />
              </div>
              <UsersManagementSection adminSecret={adminSecret} token={token} />
              <LeadsAndTrialsSection adminSecret={adminSecret} token={token} />
            </Bloque>

            {/* Técnico: no se borra, sirve cuando algo se rompe */}
            <section className="mb-8">
              <button
                type="button"
                onClick={() => setTecnicoAbierto(!tecnicoAbierto)}
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"
                data-testid="tecnico-toggle"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${tecnicoAbierto ? 'rotate-180' : ''}`} />
                Técnico: analítica del checkout, últimos pagos y auditoría de datos
              </button>
              {tecnicoAbierto && (
                <div className="mt-6">
                  <CheckoutAnalyticsSection adminSecret={adminSecret} token={token} />
                  <UltimosPagos pedir={pedir} />
                  <DataQualityAuditSection adminSecret={adminSecret} token={token} />
                </div>
              )}
            </section>

            <p className="text-slate-600 text-xs text-center">Generado: {fmtFecha(panel.generado, true)}</p>
          </div>
        )}
      </div>
    </div>
  );
}
